import { v4 as uuidv4 } from "uuid"; // https://github.com/tc39/proposal-uuid
import React, { createContext, useContext, useEffect, useState } from "react";
import { IHistoryPayload } from "@ln/litigationanalytics-abstractions";
import { lexisPlusServices, entityTypeForName } from "services/lexis";
import { parse, stringify } from "query-string";

interface LexisHistoryContextInterface {
  usePageHistory: Function;
  updatePageHistory: Function;
  pageTitle: string;
  entityType: string;
}

interface HistoryChangeParams {
  crid?: string;
  entityType?: string;
  pageTitle?: string;
}

const LexisHistoryContext =
  createContext<LexisHistoryContextInterface | null>(null);

const useLexisHistory = () => useContext(LexisHistoryContext);

function triggerHistoryEvent(event: IHistoryPayload) {
  lexisPlusServices.saveToHistory(event);
  if ("development" === process.env.NODE_ENV) {
    console.log(event);
  }
}

function getUrlCrid() {
  const { search } = window.location;
  const params = parse(search);
  return "crid" in params
    ? Array.isArray(params.crid)
      ? params.crid[0]
      : params.crid
    : "";
}

const DEFAULT = {
  pageTitle: "",
  entityType: "",
  crid: uuidv4(),
};

const LOADING_TITLE = "Loading...";

const LexisHistoryProvider: React.FC = ({ children }) => {
  const [lexisHistory, setLexisHistory] = useState(DEFAULT);
  const { entityType, pageTitle, crid } = lexisHistory;
  const [lastId, setLastId] = useState("");

  function getViewId() {
    const { pathname, search } = window.location;
    const params = parse(search);
    const remove = [
      "crid",
      "cbc",
      "transid",
      "aspxerrorpath",
      "pdmfid",
      "ecomp",
      "prid",
    ];
    remove.forEach((key) => {
      if (key in params) delete params[key];
    });
    return `${pathname}?${stringify(params)}`;
  }

  function usePageHistory(newEntityType: string, newPageTitle: string) {
    useEffect(() => {
      updatePageHistory(newEntityType, newPageTitle);
    }, [newEntityType, newPageTitle]);
  }

  function updatePageHistory(newEntityType: string, newPageTitle: string) {
    const currentCrid = getUrlCrid();
    let delta: HistoryChangeParams = {};
    if (currentCrid && currentCrid !== crid) delta.crid = currentCrid;
    if (newEntityType !== entityType) delta.entityType = newEntityType;
    if (newPageTitle !== pageTitle) delta.pageTitle = newPageTitle;
    const hasChanges = Object.keys(delta).length > 0;
    const isLoading = newPageTitle.startsWith(LOADING_TITLE);
    if (hasChanges && !isLoading)
      setLexisHistory({
        ...lexisHistory,
        ...delta,
      });
  }

  useEffect(() => {
    function getUrl() {
      const { href, search, pathname } = window.location;
      return search.includes("crid")
        ? href
        : `${pathname}${search.length > 0 ? `${search}&` : "?"}crid=${crid}`;
    }
    function recordHistory(newEntityType: string, newPageTitle: string) {
      const url = getUrl();
      const viewId = getViewId();
      const activityId = uuidv4();
      const event = {
        entityType: entityTypeForName(newEntityType),
        url,
        researchThreadId: activityId,
        activityId: activityId,
        parentActivityId: activityId,
        title: pageTitle,
        viewId,
      };
      if (lastId !== viewId) {
        triggerHistoryEvent(event);
        setLastId(viewId);
      }
    }
    if (entityType && pageTitle) {
      recordHistory(entityType, pageTitle);
    }
  }, [entityType, pageTitle, crid, lastId]);

  return (
    <LexisHistoryContext.Provider
      value={{ usePageHistory, pageTitle, entityType, updatePageHistory }}
    >
      {children}
    </LexisHistoryContext.Provider>
  );
};

export { LexisHistoryProvider, useLexisHistory, LOADING_TITLE };
